/**
 * User Overview Tab
 * Displays user profile, status, and basic information
 */

import { User } from '../../api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { StatsGrid, StatsCard } from '@/components/shared/StatsCard'
import {
  User as UserIcon,
  Mail,
  Shield,
  Globe,
  CheckCircle,
  XCircle,
  Calendar,
  Coins,
  Bot,
  Activity,
  DollarSign,
} from 'lucide-react'
import { formatDate, formatCurrency, formatNumber } from '@/lib/utils/format'

interface UserOverviewTabProps {
  user: User
}

export function UserOverviewTab({ user }: UserOverviewTabProps) {
  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <StatsGrid>
        <StatsCard
          title="Token Credits"
          value={user.tokenCredits || 0}
          icon={<Coins className="h-4 w-4" />}
          format="number"
        />
        <StatsCard
          title="Total Agents"
          value={user.total_agents || 0}
          icon={<Bot className="h-4 w-4" />}
          format="number"
        />
        <StatsCard
          title="Tokens Consumed"
          value={user.tokens_consumed || 0}
          icon={<Activity className="h-4 w-4" />}
          format="number"
        />
        <StatsCard
          title="Total Cost"
          value={user.cost || 0}
          icon={<DollarSign className="h-4 w-4" />}
          format="currency"
        />
      </StatsGrid>

      {/* Profile Information */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
          <CardDescription>Basic user account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <UserIcon className="h-4 w-4" />
                Name
              </div>
              <div className="text-sm">{user.name || user.username || 'Not set'}</div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Mail className="h-4 w-4" />
                Email
              </div>
              <div className="text-sm font-mono">{user.email}</div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Shield className="h-4 w-4" />
                Role
              </div>
              <Badge variant={user.role === 'ADMIN' ? 'default' : 'secondary'}>
                {user.role}
              </Badge>
            </div>

            {user.provider && (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Globe className="h-4 w-4" />
                  Auth Provider
                </div>
                <div className="text-sm capitalize">{user.provider}</div>
              </div>
            )}

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                {user.emailVerified ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-muted-foreground" />
                )}
                Email Verified
              </div>
              <Badge variant={user.emailVerified ? 'default' : 'secondary'}>
                {user.emailVerified ? 'Yes' : 'No'}
              </Badge>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Member Since
              </div>
              <div className="text-sm">{formatDate(user.createdAt || '')}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Account Metadata</CardTitle>
          <CardDescription>System information and timestamps</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground">User ID</div>
              <div className="text-xs font-mono text-muted-foreground break-all">
                {user._id}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground">Created At</div>
              <div className="text-sm">{formatDate(user.createdAt || '')}</div>
            </div>

            {user.updatedAt && (
              <div className="space-y-1">
                <div className="text-sm font-medium text-muted-foreground">
                  Last Updated
                </div>
                <div className="text-sm">{formatDate(user.updatedAt)}</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
